import { DatePickerRange } from "@/components/ui/date-picker-range";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginatedResponse } from "@/types/common";
import { useDebounce } from "@/utils/misc";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
    ColumnDef,
    ColumnFiltersState,
    FilterFn,
    PaginationState,
    Row,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    useReactTable
} from "@tanstack/react-table";
import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { DateRange } from "react-day-picker";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "../input";
import { DataTablePagination } from "./pagination";

interface DataTableFilter {
    type: "input" | "select" | "date-range";
    column?: string | null;
    key?: string | null;
    placeholder?: string | null;
    options?: { label: string; value: string; icon?: any }[] | null;
}

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data?: TData[];
    paginatedData?: PaginatedResponse<TData>;
    filters?: DataTableFilter[];
    dynamicPagination?: boolean;
    onRowClick?: (row: Row<TData>) => void;
    onRowClickNavigateTo?: (row: Row<TData>) => string;
    bodyClassName?: string;
}

export function DataTable<TData, TValue>({ columns, data, paginatedData, filters, onRowClick, onRowClickNavigateTo, bodyClassName = "h-[370px]" }: DataTableProps<TData, TValue>) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: parseInt(searchParams.get("page") || "1") - 1,
        pageSize: parseInt(searchParams.get("page_size") || "25")
    });
    const [sorting, setSorting] = React.useState<SortingState>(
        searchParams.get("o")
            ? [
                  {
                      desc: Boolean(searchParams.get("o")?.includes("-")),
                      id: searchParams.get("o") ?? ""
                  }
              ]
            : []
    );
    const [globalFilter, setGlobalFilter] = React.useState<string>("");
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});

    // Add debounced state only for search/smart_search filters
    const [searchInputs, setSearchInputs] = React.useState<Record<string, string>>({});
    const debouncedSearchInputs = useDebounce(searchInputs, 600);

    const fuzzyFilter: FilterFn<TData> = (row, columnId, value, addMeta) => {
        // Rank the item
        const itemRank = rankItem(row.getValue(columnId), value);

        // Store the itemRank info
        addMeta({
            itemRank
        });

        // Return if the item should be filtered in/out
        return itemRank.passed;
    };

    useEffect(() => {
        if (sorting[0]) {
            searchParams.set("o", sorting[0].desc ? `-${sorting[0].id}` : sorting[0].id);
            setSearchParams(searchParams);
        }
    }, [sorting]);

    useEffect(() => {
        for (const colFilter of columnFilters) {
            searchParams.set(`${colFilter.id}`, `${colFilter.value}`);
        }
        setSearchParams(searchParams);
    }, [columnFilters]);

    useEffect(() => {
        if (paginatedData) {
            searchParams.set("page", (pagination.pageIndex + 1).toString());
            searchParams.set("page_size", pagination.pageSize.toString());
            setSearchParams(searchParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination]);

    // Update search params when debounced search inputs change
    useEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        let hasChanges = false;

        Object.entries(debouncedSearchInputs).forEach(([key, value]) => {
            if (value) {
                newSearchParams.set(key, value);
                hasChanges = true;
            } else {
                newSearchParams.delete(key);
                hasChanges = true;
            }
        });

        if (hasChanges) {
            setSearchParams(newSearchParams);
        }
    }, [debouncedSearchInputs]);

    const table = useReactTable({
        data: paginatedData ? paginatedData.results : data ? data : [],
        columns: columns,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: paginatedData ? true : false,
        onPaginationChange: setPagination,
        getPaginationRowModel: !paginatedData ? getPaginationRowModel() : undefined,
        rowCount: paginatedData ? paginatedData.count : undefined,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            pagination,
            globalFilter,
            columnFilters,
            columnVisibility
        },
        initialState: {
            pagination: {
                pageSize: 25
            }
        }
    });

    return (
        <div>
            {filters?.length && (
                <div className="flex items-center space-x-2 py-4">
                    {filters?.map((filter, index) => {
                        if (filter.type == "input") {
                            if (filter.column || (!filter.column && !filter.key)) {
                                console.log("filter", filter);
                                return (
                                    <Input
                                        key={index}
                                        placeholder={filter.placeholder ? filter.placeholder : "Rechercher..."}
                                        value={filter.column ? ((table.getColumn(filter.column)?.getFilterValue() as string) ?? "") : (globalFilter ?? "")}
                                        onChange={event => (filter.column ? table.getColumn(filter.column)?.setFilterValue(event.target.value) : setGlobalFilter(event.target.value))}
                                        className="max-w-sm"
                                    />
                                );
                            }

                            if (filter.key) {
                                // Check if this is a search filter that should be debounced
                                const isSearchFilter = filter.key === "search" || filter.key === "smart_search";

                                if (isSearchFilter) {
                                    return (
                                        <Input
                                            key={index}
                                            placeholder={filter.placeholder ? filter.placeholder : "Rechercher..."}
                                            value={searchInputs[filter.key] ?? searchParams.get(filter.key as string) ?? ""}
                                            onChange={event => {
                                                setSearchInputs(prev => ({
                                                    ...prev,
                                                    [filter.key as string]: event.target.value
                                                }));
                                            }}
                                            className="max-w-sm"
                                        />
                                    );
                                } else {
                                    // Non-search filters update immediately
                                    return (
                                        <Input
                                            key={index}
                                            placeholder={filter.placeholder ? filter.placeholder : "Rechercher..."}
                                            value={searchParams.get(filter.key as string) ?? ""}
                                            onChange={event => {
                                                let _searchParams = new URLSearchParams(searchParams);
                                                if (event.target.value) {
                                                    _searchParams.set(filter.key as string, event.target.value);
                                                } else {
                                                    _searchParams.delete(filter.key as string);
                                                }
                                                setSearchParams(_searchParams);
                                            }}
                                            className="max-w-sm"
                                        />
                                    );
                                }
                            }
                        } else if (filter.type == "select" && filter.options) {
                            if (filter.column) {
                                return (
                                    <Select
                                        key={index}
                                        value={String(table.getColumn(filter.column)?.getFilterValue() ?? "")}
                                        onValueChange={event => {
                                            if (filter.column) {
                                                if (event === "all") {
                                                    table.getColumn(filter.column)?.setFilterValue(undefined);
                                                    searchParams.delete(filter.column);
                                                    setSearchParams(searchParams);
                                                } else {
                                                    table.getColumn(filter.column)?.setFilterValue(event);
                                                }
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder={filter.placeholder} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {filter.options.map((option, i) => (
                                                <SelectItem key={`${option.value}-${i}`} value={option.value}>
                                                    <div className="flex flex-row space-x-2">
                                                        <div>{option.icon}</div>
                                                        <div>{option.label}</div>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                );
                            }

                            if (filter.key) {
                                return (
                                    <Select
                                        key={index}
                                        value={searchParams.get(filter.key) ?? ""}
                                        onValueChange={event => {
                                            if (event === "all" && filter.key) {
                                                searchParams.delete(filter.key);
                                                setSearchParams(searchParams);
                                            } else {
                                                let _searchParams = new URLSearchParams(searchParams);
                                                if (event) {
                                                    _searchParams.set(filter.key as string, event);
                                                } else {
                                                    _searchParams.delete(filter.key as string);
                                                }
                                                setSearchParams(_searchParams);
                                            }
                                        }}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder={filter.placeholder} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {filter.options.map(option => (
                                                <SelectItem value={option.value}>
                                                    <div className="flex flex-row space-x-2">
                                                        <div>{option.icon}</div>
                                                        <div>{option.label}</div>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                );
                            }
                        } else if (filter.type == "date-range") {
                            if (filter.column) {
                                return (
                                    <DatePickerRange
                                        key={index}
                                        date={table.getColumn(filter.column)?.getFilterValue() as DateRange}
                                        setDate={(date?: DateRange) => {
                                            if (date && filter.column) {
                                                table.getColumn(filter.column)?.setFilterValue(date);
                                            }
                                        }}
                                    />
                                );
                            }

                            if (filter.key) {
                                return (
                                    <DatePickerRange
                                        key={index}
                                        date={
                                            searchParams.get(`${filter.key}__range`)
                                                ? {
                                                      from: DateTime.fromISO(searchParams.get(`${filter.key}__range`)?.split(",")[0] as string, { zone: "utc" }).toJSDate(),
                                                      to: DateTime.fromISO(searchParams.get(`${filter.key}__range`)?.split(",")[1] as string, { zone: "utc" }).toJSDate()
                                                  }
                                                : undefined
                                        }
                                        setDate={(date?: DateRange) => {
                                            if (date && filter.key) {
                                                let _searchParams = new URLSearchParams(searchParams);
                                                if (date.from && date.to) {
                                                    // Convert to YYYY-MM-DD format without timezone conversion
                                                    const formatDate = (d: Date) => {
                                                        return DateTime.fromJSDate(d).toFormat("yyyy-MM-dd");
                                                    };
                                                    _searchParams.set(`${filter.key}__range`, `${formatDate(date.from)},${formatDate(date.to)}`);
                                                } else {
                                                    _searchParams.delete(`${filter.key}__range`);
                                                }
                                                setSearchParams(_searchParams);
                                            }
                                        }}
                                    />
                                );
                            }
                        }
                    })}
                </div>
            )}
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map(header => {
                                    return <TableHead key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</TableHead>;
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody className={bodyClassName}>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map(row => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                    className={onRowClick || onRowClickNavigateTo ? "cursor-pointer" : ""}
                                    onClick={event => {
                                        if (onRowClick) {
                                            onRowClick(row);
                                        } else if (onRowClickNavigateTo) {
                                            const navigateTo = onRowClickNavigateTo(row);
                                            if (event.metaKey || event.ctrlKey) {
                                                const win = window.open(navigateTo, "_blank");
                                                win?.focus();
                                            } else {
                                                navigate(navigateTo);
                                            }
                                        }
                                    }}
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-[370px] text-center">
                                    No result.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="mt-3">
                <DataTablePagination table={table} />
            </div>
        </div>
    );
}
